import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mxn, fmtDate } from "@/lib/api";
import { DocumentSheet } from "@/components/DocumentSheet";
import { DocDetailHeader } from "@/components/DocDetailHeader";
import { PrintRemisionDialog } from "@/components/PrintRemisionDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/Kit";
import { Printer, Download, Copy, Pencil, Trash2 } from "lucide-react";

export default function RemisionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState({ name: "TAS Publicidad 360" });
  const [printOpen, setPrintOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    api.get(`/remisiones/${id}`).then((r) => setDoc(r.data)).catch(() => setDoc(false));
    api.get("/settings").then((r) => setCompany(r.data.company || {})).catch(() => {});
  }, [id]);

  useEffect(() => {
    document.body.style.background = "#EEF2F6";
    return () => (document.body.style.background = "");
  }, []);

  const doDelete = async () => {
    try {
      await api.delete(`/remisiones/${id}`);
      toast.success("Remisión eliminada");
      navigate("/remisiones");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  if (doc === null) return <div className="p-10 font-mono-data text-[#5B6672]">Cargando…</div>;
  if (doc === false) return <div className="p-10 font-mono-data text-[#ED1F26]">Remisión no encontrada.</div>;

  const c = doc.client || {};
  const totalUnits = (doc.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const balance = Number(doc.balance ?? 0);
  const paid = Number(doc.amount_paid ?? 0);

  const actions = [
    { label: "Imprimir", icon: <Printer size={16} />, onClick: () => setPrintOpen(true), testid: "action-imprimir" },
    { label: "Descargar PDF", icon: <Download size={16} />, onClick: () => setPrintOpen(true), testid: "action-descargar" },
    { label: "Duplicar", icon: <Copy size={16} />, onClick: () => navigate(`/remisiones?duplicate=${id}`), testid: "action-duplicar" },
    { label: "Editar", icon: <Pencil size={16} />, onClick: () => navigate(`/remisiones?edit=${id}`), testid: "action-editar" },
    { label: "Eliminar", icon: <Trash2 size={16} />, onClick: () => setConfirmDel(true), danger: true, testid: "action-eliminar" },
  ];

  return (
    <div className="min-h-screen data-emphasis" data-testid="remision-detalle">
      <DocDetailHeader
        title={`Remisión ${doc.number}`}
        subtitle={c.name || "—"}
        onClose={() => navigate(-1)}
        actions={actions}
      />

      <div className="grid lg:grid-cols-[1fr_380px]">
        {/* Documento a la izquierda */}
        <div className="p-6 lg:p-10 overflow-auto">
          <DocumentSheet doc={doc} company={company} type="remision" variant="screen" />
        </div>

        {/* Panel de información a la derecha */}
        <aside className="border-l border-[#E3E8EE] bg-white min-h-[calc(100vh-4rem)] p-6 space-y-6">
          <div className="flex items-center justify-between">
            <span className="overline">Total</span>
            <span className="font-mono-data text-2xl font-bold text-[#16202B]" data-testid="detalle-total">{mxn(doc.total)}</span>
          </div>

          <div className="flex items-center justify-between border-t border-[#E3E8EE] pt-4">
            <span className="font-mono-data text-sm text-[#5B6672]">Estatus</span>
            <StatusBadge status={doc.status} />
          </div>

          <div className="space-y-3 border-t border-[#E3E8EE] pt-4">
            <Field label="Número" value={doc.number} />
            <Field label="Contacto" value={c.name || "—"} />
            <Field label="Fecha" value={fmtDate(doc.created_at)} />
            {doc.due_date && <Field label="Vencimiento" value={fmtDate(doc.due_date)} />}
            <Field label="Total unidades" value={totalUnits} />
            <Field label="Requiere factura" value={doc.requiere_factura ? "Sí" : "No"} />
            {doc.ot_numbers && doc.ot_numbers.length > 0 && (
              <Field label="OT vinculada" value={doc.ot_numbers.join(", ")} testid="detalle-ot" />
            )}
          </div>

          <div className="border-t border-[#E3E8EE] pt-4">
            <div className="overline mb-3">Cobranza</div>
            <div className="flex items-center justify-between">
              <span className="font-mono-data text-sm text-[#5B6672]">{balance > 0.009 ? "Pendiente de pago" : "Pagado"}</span>
              <span className={`font-mono-data text-sm font-semibold ${balance > 0.009 ? "text-[#ED1F26]" : "text-[#49AC9A]"}`}>
                {mxn(balance > 0.009 ? balance : (paid || doc.total))}
              </span>
            </div>
            {balance > 0.009 && (
              <button onClick={() => navigate("/cobranza")} data-testid="detalle-add-payment" className="mt-3 font-mono-data text-sm text-[#465B7F] hover:underline">
                Registrar pago en Cobranza →
              </button>
            )}
          </div>

          {doc.tags && doc.tags.length > 0 && (
            <div className="border-t border-[#E3E8EE] pt-4">
              <div className="overline mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {doc.tags.map((t) => (
                  <span key={t} className="font-mono-data text-xs bg-[#E9EEF3] dark:bg-[#465B7F]/50 text-[#465B7F] dark:text-slate-100 px-2 py-1 rounded">{t}</span>
                ))}
              </div>
            </div>
          )}

          {doc.notes && (
            <div className="border-t border-[#E3E8EE] pt-4">
              <div className="overline mb-2">Notas</div>
              <p className="font-mono-data text-sm text-[#16202B]">{doc.notes}</p>
            </div>
          )}
        </aside>
      </div>

      <PrintRemisionDialog open={printOpen} onClose={() => setPrintOpen(false)} remisionId={id} />
      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={doDelete}
        title="Eliminar remisión"
        message={`¿Eliminar la remisión ${doc.number}? Esta acción no se puede deshacer.`}
        testid="confirm-delete-remision"
      />
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-4" data-testid={testid}>
      <span className="font-mono-data text-sm text-[#5B6672]">{label}</span>
      <span className="font-mono-data text-sm text-[#16202B] text-right">{value}</span>
    </div>
  );
}
